import { combineLatest, map, Observable, of, switchMap } from 'rxjs';

import { Injectable } from '@angular/core';
import { and, collectionData, doc, docData, DocumentReference, or, orderBy, query, Timestamp, where } from '@angular/fire/firestore';
import { FormGroup } from '@angular/forms';
import { IInventoryCategory, IInventoryItem, IInventoryItemWithCategory } from '@inventory/models/inventory';
import { IInventoryItemCreateForm, IInventoryItemEditForm } from '@inventory/models/inventory-forms';
import { FireStoreCollection } from '@shared/models/firestore';
import { FirestoreService } from '@shared/services/firestore/firestore.service';

@Injectable({
	providedIn: 'root',
})
export class InventoryItemsService extends FirestoreService<IInventoryItem> {
	constructor() {
		super(FireStoreCollection.INVENTORY_ITEMS);
	}

	getInventoryItemNamesByOrganisationId(organisationId: string): Observable<Array<string>> {
		const organisationRef = doc(this.firestore, `${FireStoreCollection.ORGANISATIONS}/${organisationId}`) as DocumentReference;
		const inventoryQuery = query(
			this.collectionRef,
			where('organisation', '==', organisationRef),
			where('archived', '==', false),
			orderBy('createdDate', 'desc'),
		);

		return collectionData<IInventoryItem>(inventoryQuery, { idField: 'id' }).pipe(map((items) => items.map((item) => item.name)));
	}

	getInventoryItemsByOrganisation(
		organisationRef: DocumentReference,
		searchTerm: string,
		showArchived: boolean = false,
	): Observable<Array<IInventoryItem>> {
		let inventoryQuery = query(this.collectionRef, where('organisation', '==', organisationRef));
		if (searchTerm) {
			const lowerCaseSearchTerm = searchTerm.toLocaleLowerCase();
			inventoryQuery = query(
				inventoryQuery,
				or(
					and(where('nameLower', '>=', lowerCaseSearchTerm), where('nameLower', '<=', lowerCaseSearchTerm + '\uf8ff')),
					and(where('skuLower', '>=', lowerCaseSearchTerm), where('skuLower', '<=', lowerCaseSearchTerm + '\uf8ff')),
				),
			);
		}

		inventoryQuery = query(inventoryQuery, where('archived', '==', showArchived));

		inventoryQuery = query(inventoryQuery, orderBy('createdDate', 'desc'));

		return collectionData<IInventoryItem>(inventoryQuery, { idField: 'id' });
	}

	getInventoryItemsWithCategoryByOrganisation(
		organisationRef: DocumentReference,
		searchTerm: string,
		showArchived: boolean = false,
	): Observable<Array<IInventoryItemWithCategory>> {
		let inventoryQuery = query(this.collectionRef, where('organisation', '==', organisationRef));

		if (searchTerm) {
			const lowerCase = searchTerm.toLocaleLowerCase();
			inventoryQuery = query(
				inventoryQuery,
				or(
					and(where('nameLower', '>=', lowerCase), where('nameLower', '<=', lowerCase + '\uf8ff')),
					and(where('skuLower', '>=', lowerCase), where('skuLower', '<=', lowerCase + '\uf8ff')),
				),
			);
		}

		inventoryQuery = query(inventoryQuery, where('archived', '==', showArchived));
		inventoryQuery = query(inventoryQuery, orderBy('createdDate', 'desc'));

		return collectionData<IInventoryItem>(inventoryQuery, { idField: 'id' }).pipe(
			switchMap((items) => {
				const categoryRefs = Array.from(new Set(items.map((i) => i.category).filter((ref): ref is DocumentReference => !!ref)));

				if (categoryRefs.length === 0) {
					return of(items.map((i) => ({ ...i, categoryData: null })));
				}

				const categoryObservables = categoryRefs.map((ref) =>
					docData<IInventoryCategory>(ref as DocumentReference<IInventoryCategory>, { idField: 'id' }),
				);

				return combineLatest(categoryObservables).pipe(
					map((categories) => {
						const categoryMap = new Map(categoryRefs.map((ref, i) => [ref.path, categories[i]]));
						return items.map((item) => ({
							...item,
							categoryData: item.category ? categoryMap.get(item.category.path) || null : null,
						}));
					}),
				);
			}),
		);
	}

	async createInventoryItem(
		organisationRef: DocumentReference,
		inventoryItem: FormGroup<IInventoryItemCreateForm>,
	): Promise<DocumentReference<IInventoryItem>> {
		const categoryId = inventoryItem.controls.category.value;
		let inventoryCategoryRef: DocumentReference | null = null;
		if (categoryId) {
			inventoryCategoryRef = doc(this.firestore, `${FireStoreCollection.INVENTORY_CATEGORIES}/${categoryId}`);
		}
		const nameValue = inventoryItem.controls.name.value as string;
		const skuValue = inventoryItem.controls.sku.value as string;
		const descriptionValue = inventoryItem.controls.description.value as string | null;
		const costPriceValue = Number(inventoryItem.controls.costPrice.value) || null;
		const sellPriceValue = Number(inventoryItem.controls.sellPrice.value) || null;

		return this.create({
			name: nameValue,
			nameLower: nameValue.toLocaleLowerCase(),
			sku: skuValue,
			skuLower: skuValue.toLocaleLowerCase(),
			description: descriptionValue || null,
			category: inventoryCategoryRef || null,
			archived: false,
			costPrice: costPriceValue,
			sellPrice: sellPriceValue,
			totalQuantity: 0,
			organisation: organisationRef,
			createdDate: Timestamp.now(),
			updatedDate: Timestamp.now(),
		});
	}

	async updateInventoryItem(inventoryItemId: string, inventoryItem: FormGroup<IInventoryItemEditForm>): Promise<void> {
		const categoryId = inventoryItem.controls.category.value;
		let inventoryCategoryRef: DocumentReference | null = null;
		if (categoryId) {
			inventoryCategoryRef = doc(this.firestore, `${FireStoreCollection.INVENTORY_CATEGORIES}/${categoryId}`);
		}
		const nameValue = inventoryItem.controls.name.value as string;
		const skuValue = inventoryItem.controls.sku.value as string;
		const descriptionValue = inventoryItem.controls.description.value as string | null;
		const costPriceValue = Number(inventoryItem.controls.costPrice.value) || null;
		const sellPriceValue = Number(inventoryItem.controls.sellPrice.value) || null;
		const archivedValue = inventoryItem.controls.archived.value as boolean | null;

		return this.update(inventoryItemId, {
			name: nameValue,
			nameLower: nameValue.toLocaleLowerCase(),
			sku: skuValue,
			skuLower: skuValue.toLocaleLowerCase(),
			description: descriptionValue || null,
			category: inventoryCategoryRef || null,
			costPrice: costPriceValue,
			sellPrice: sellPriceValue,
			archived: archivedValue ?? false,
			updatedDate: Timestamp.now(),
		});
	}

	async archiveInventoryItem(inventoryItemId: string): Promise<void> {
		return this.update(inventoryItemId, {
			archived: true,
			updatedDate: Timestamp.now(),
		});
	}

	async unArchiveInventoryItem(inventoryItemId: string): Promise<void> {
		return this.update(inventoryItemId, {
			archived: false,
			updatedDate: Timestamp.now(),
		});
	}
}
